import React, { useEffect, useState } from 'react';
import { Box, InputAdornment, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { CustomButton } from '../../../../components/CustomButton';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../../constants/routes';
import { CustomInput } from '../../../../components/CustomInput';
import { ReactComponent as SearchIcon } from '../../../../assets/images/search.svg';
import { getEventDeclensionForm } from '../../../../utils/getEventDeclension';
import EnhancedTable from '../../../../components/EnhancedTable';
import { PARTNERS_HEAD_CELLS, PARTNERS_STATUSES } from '../../constants/index';
import { CustomModal } from '../../../../components/CustomModal';
import {
  actionsArchivePartner,
  archivePartner,
  getCities,
  getPartners,
  unarchivePartner,
  updatePartnersPositions
} from '../../../../store/partners/actions';
import { NotFoundItems } from '../../../../components/NotFoundItems';
import { CustomSelect } from '../../../../components/CustomSelect';
import { FirstTextSelect } from '../../../../components/FirstTextSelcet';
import { useDebounce } from 'use-lodash-debounce';
import { ReactComponent as ReportRedIcon } from '../../../../assets/images/report_red.svg';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { PartnersTableBodyWithDnD } from '../PartnersTableBody/PartnersTableBodyWithDnD';

const propsSelect = (name, text, options, value) => ({
  name,
  options,
  value,
  startAdornmentText: <FirstTextSelect text={text} />,
  size: 'small',
  variant: 'outlined'
});

export const PartnersTab = () => {
  const dispatch = useDispatch();
  const { userPermissions, currentUsersPermissions } = useSelector(
    (state) => state.userPermissions
  );
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);
  const {
    partners,
    fetching,
    cities: branches,
    errorArchive
  } = useSelector((state) => state.partners);
  const cities = branches
    ?.map(
      (city, index, array) => array.findIndex((item) => city.name === item.name) === index && city
    )
    .filter((city) => city)
    .map((city) => ({ label: city?.name, value: city?.id }));
  cities?.unshift({ label: 'Все', value: 'all' });

  const isAllowedToList = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNERS.listPartners.value
  );
  const isAllowedToCreate = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNERS.createPartners.value
  );
  const isAllowedToRetrieve = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNERS.retrievePartners.value
  );
  const isAllowedToArchive = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNERS.archivePartners.value
  );
  const isAllowedToEdit = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNERS.updatePartners.value
  );

  const [filter, setFilter] = useState({
    order_by: 'position_index',
    order_type: 'asc',
    search: '',
    status: PARTNERS_STATUSES[1],
    city_id: cities[0] || { value: 'all', label: 'Все' },
    page: 0,
    size: 10
  });
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [isBackModal, setIsBackModal] = useState(false);
  const [curID, setCurID] = useState(0);
  const [errorModal, setErrorModal] = useState(false);

  useEffect(
    () => {
      isAllowedToList && dispatch(getCities());
    }, // eslint-disable-next-line
    []
  );

  useEffect(() => {
    if (errorArchive) setErrorModal(true);
  }, [errorArchive]);

  const debouncedValue = useDebounce(filter.search, 500);

  useEffect(() => {
    isAllowedToList &&
      dispatch(
        getPartners({
          ...filter,
          search: filter.search !== '' ? filter.search : undefined,
          page: filter.page + 1,
          city_id: filter.city_id.value !== 'all' ? filter.city_id.value : undefined,
          status:
            filter.status.value !== PARTNERS_STATUSES[0].value ? filter.status.value : undefined
        })
      );
    // eslint-disable-next-line
  }, [
    filter.city_id,
    filter.page,
    filter.status,
    debouncedValue,
    filter.order_type,
    filter.size,
    filter.order_by
  ]);

  const handlerOnChange = (e, field) => setFilter({ ...filter, page: 0, [field]: e });

  return (
    <Box data-testid="PartnersTab">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <CustomButton
          component={Link}
          to={ROUTES.PARTNERS_ADD_PARTNER}
          size="small"
          mr="24px"
          variant="contained"
          disabled={!isAllowedToCreate}
          tooltip={isAllowedToCreate ? null : DISABLED_CONTROL_TOOLTIP}
          onClick={
            isAllowedToCreate ? () => localStorage.setItem('partnersTab', 'PARTNERS') : undefined
          }>
          Добавить
        </CustomButton>

        <Box
          sx={{ width: '855px' }}
          display="flex"
          justifyContent="space-between"
          alignItems="center">
          <CustomSelect
            {...propsSelect('status', 'Статус:', PARTNERS_STATUSES, filter.status)}
            sx={{ width: '240px' }}
            onChange={(e) => handlerOnChange(e, 'status')}
            disabled={!isAllowedToList}
            tooltip={isAllowedToList ? null : DISABLED_CONTROL_TOOLTIP}
          />

          <CustomSelect
            {...propsSelect('city_id', 'Город:', cities, filter.city_id)}
            sx={{ width: '240px' }}
            onChange={(e) => handlerOnChange(e, 'city_id')}
            disabled={!isAllowedToList}
            tooltip={isAllowedToList ? null : DISABLED_CONTROL_TOOLTIP}
          />

          <CustomInput
            value={filter.search}
            disabled={!isAllowedToList}
            tooltip={isAllowedToList ? null : DISABLED_CONTROL_TOOLTIP}
            onChange={(e) => setFilter({ ...filter, search: e.target.value, page: 0 })}
            size="small"
            variant="outlined"
            type="search"
            placeholder="Поиск"
            sx={{ width: '343px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Box>

      {partners?.items?.length && isAllowedToList ? (
        <>
          <Typography color="text.secondary" m="24px 0px">
            Найдено {getEventDeclensionForm(partners?.items?.length, 'partners')}
          </Typography>

          <EnhancedTable
            rows={partners?.items}
            headCells={PARTNERS_HEAD_CELLS}
            filter={filter}
            total={partners?.total || 0}
            fetching={fetching}
            heightRow={77}
            setFilter={setFilter}
            showPagination={false}>
            <PartnersTableBodyWithDnD
              data={partners?.items}
              setData={(data) => {
                dispatch(updatePartnersPositions(data));
              }}
              setCurID={setCurID}
              openArchiveModal={() => setIsSuccessModal(true)}
              openUnarchiveModal={() => setIsBackModal(true)}
              cities={branches}
              permissions={{
                retrieve: isAllowedToRetrieve,
                edit: isAllowedToEdit,
                archive: isAllowedToArchive
              }}
            />
          </EnhancedTable>
        </>
      ) : (
        <NotFoundItems
          message={isAllowedToList ? undefined : 'Вам запрещён доступ к просмотру списка партнёров'}
        />
      )}

      <CustomModal
        opened={isSuccessModal}
        modalClose={() => setIsSuccessModal(false)}
        modalSubmit={() => {
          dispatch(archivePartner(curID));
          dispatch(
            getPartners({
              ...filter,
              search: filter.search !== '' ? filter.search : undefined,
              page: filter.page + 1,
              city_id: filter.city_id.value !== 'all' ? filter.city_id.value : undefined,
              status:
                filter.status.value !== PARTNERS_STATUSES[0].value ? filter.status.value : undefined
            })
          );
          setIsSuccessModal(false);
        }}
        text={`Вы действительно хотите добавить компанию ${
          curID && partners?.items?.find((item) => item?.id === curID)?.name
        } в Архив?`}
        cancelBtnName="Отмена"
        submitBtnName="Да"
        id="Warning Modal"
      />
      <CustomModal
        opened={isBackModal}
        modalClose={() => setIsBackModal(false)}
        modalSubmit={() => {
          dispatch(unarchivePartner(curID));
          dispatch(
            getPartners({
              ...filter,
              search: filter.search !== '' ? filter.search : undefined,
              page: filter.page + 1,
              city_id: filter.city_id.value !== 'all' ? filter.city_id.value : undefined,
              status:
                filter.status.value !== PARTNERS_STATUSES[0].value ? filter.status.value : undefined
            })
          );
          setIsBackModal(false);
        }}
        text={`Вы действительно хотите вернуть компанию ${
          curID && partners?.items?.find((item) => item?.id === curID)?.name
        } в работу?`}
        cancelBtnName="Отмена"
        submitBtnName="Да"
      />
      <CustomModal
        opened={errorModal}
        modalClose={() => {
          setErrorModal(false);
          dispatch(actionsArchivePartner.fail(null));
        }}
        text={errorArchive || ''}
        cancelBtnName="Отмена"
        withoutSubmit
        icon={<ReportRedIcon />}
      />
    </Box>
  );
};
